import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  recordMemos: { path: '/class-record-memos', pageLabel: '기록 모아보기' },
  weeklyGuide: { path: '/weekly-learning-guide', pageLabel: '주간학습 안내' },
  homeNoticeModal: {
    path: '/weekly-learning-guide',
    openSelector: '.cp-weekly-learning-guide-home-notice-btn',
    pageLabel: '주간학습 안내',
    openLabel: '가정통신 관리',
  },
  trackColorModal: {
    path: '/weekly-learning-guide',
    openSelector: '.cp-weekly-learning-guide-track-color-btn',
    pageLabel: '주간학습 안내',
    openLabel: '시간표 색상 관리',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-record-memos': R.recordMemos,
  'record-category-tabs': R.recordMemos,
  'record-category-general': R.recordMemos,
  'record-category-lesson': R.recordMemos,
  'record-category-behavior': R.recordMemos,
  'record-group-select': R.recordMemos,
  'record-tag-query': R.recordMemos,
  'record-member-query': R.recordMemos,
  'go-weekly-guide': R.weeklyGuide,
  'weekly-week-nav': R.weeklyGuide,
  'weekly-home-notice-btn': R.weeklyGuide,
  'weekly-home-notice-modal': R.homeNoticeModal,
  'weekly-track-color-btn': R.weeklyGuide,
  'weekly-track-color-modal': R.trackColorModal,
  'weekly-neis-toggle-on': R.weeklyGuide,
  'weekly-neis-legend-diff': R.weeklyGuide,
  'weekly-neis-legend-dedicated': R.weeklyGuide,
  'weekly-neis-legend-unregistered': R.weeklyGuide,
  'weekly-neis-legend-short': R.weeklyGuide,
  'weekly-neis-xls-export': R.weeklyGuide,
};

const recordMemosWeeklyGuideRaw: TutorialDefinition = {
  id: 'record-memos-weekly-guide',
  title: '기록 모아보기 및 주간학습 안내',
  summary:
    '수업·행동 기록을 유형·그룹·태그·멤버별로 모아 보고, 주간학습 안내에서 가정통신·색상·NEIS 반별시간표를 다루는 방법을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '기록 모아보기와 주간학습 안내 튜토리얼을 시작해요! 기록을 유형·그룹별로 찾아보고, 주간학습 안내의 가정통신·색상·NEIS 반별시간표까지 순서대로 알아볼게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '기록 모아보기 · 주간학습 안내',
      placement: 'center',
    },
    {
      id: 'go-record-memos',
      mascot: '사이드바에서 "기록 모아보기"를 눌러 이동해 주세요.',
      targetSelector: '.cp-sidebar-record-memos-link',
      placement: 'right',
      section: '[기록 모아보기] 기록 조회 방법',
      sectionMascot: '유형·그룹·태그·멤버별로 기록을 조회하는 방법을 알아보는 구간이에요.',
    },
    {
      id: 'record-category-tabs',
      mascot:
        '위쪽 기록 유형 탭이에요. "종합기록"은 전체를 보고, "일반기록"은 수업·행동이 아닌 기록, "수업(학습)-평가-과제 기록"은 수업·평가·과제, "행동기록"은 행동특성 기록을 골라 볼 수 있어요. 하나씩 눌러 볼게요. 먼저 "일반기록"을 눌러 주세요.',
      targetSelector: '.cp-class-record-memos-category-tabs',
      advanceSelector: 'text:일반기록@.cp-class-record-memos-category-tab',
      advanceImmediately: true,
    },
    {
      id: 'record-category-general',
      mascot:
        '"일반기록"은 수업기록·행동특성이 아닌 일반 기록을 모아 보여 줘요. 이어서 "수업(학습)-평가-과제 기록" 탭을 눌러 주세요.',
      targetSelector: 'text:일반기록@.cp-class-record-memos-category-tab',
      advanceSelector: 'text:수업(학습)-평가-과제 기록@.cp-class-record-memos-category-tab',
      advanceImmediately: true,
    },
    {
      id: 'record-category-lesson',
      mascot:
        '"수업(학습)-평가-과제 기록"은 수업·평가·과제 기록 유형만 보여 줘요. 마지막으로 "행동기록" 탭을 눌러 주세요.',
      targetSelector: 'text:수업(학습)-평가-과제 기록@.cp-class-record-memos-category-tab',
      advanceSelector: 'text:행동기록@.cp-class-record-memos-category-tab',
      advanceImmediately: true,
    },
    {
      id: 'record-category-behavior',
      mascot:
        '"행동기록"은 행동특성 기록 유형만 보여 줘요. 유형 구분을 익혔으면 "다음"을 눌러 주세요.',
      targetSelector: 'text:행동기록@.cp-class-record-memos-category-tab',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'record-group-select',
      mascot:
        '"기록 조회 그룹"에서 "전체 그룹 및 태그" 또는 특정 그룹만 골라 조회할 수 있어요. 선택해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-class-record-memos-record-group-tabs',
      manualAdvance: true,
    },
    {
      id: 'record-tag-query',
      mascot:
        '왼쪽 "태그별 조회"에는 멤버로 등록되지 않은 이름 태그가 표시돼요. 접혀 있으면 ▶를 눌러 펼친 뒤, 태그를 누르면 그 태그가 달린 기록만 모아 볼 수 있어요. 둘러본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-class-record-memos-sidebar-members--general',
      manualAdvance: true,
    },
    {
      id: 'record-member-query',
      mascot:
        '"멤버별 조회"에서는 클래스에 등록된 멤버 이름 태그로 해당 학생 기록만 모아 볼 수 있어요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-class-record-memos-sidebar-members:not(.cp-class-record-memos-sidebar-members--general)',
      manualAdvance: true,
    },
    {
      id: 'go-weekly-guide',
      mascot: '이제 주간학습 안내로 이동해요. 사이드바에서 "주간학습 안내"를 눌러 주세요.',
      targetSelector: '.cp-sidebar-weekly-learning-guide-link',
      placement: 'right',
      section: '[주간학습 안내] 조회 및 가정통신 기록',
      sectionMascot: '주 선택과 가정통신 메시지 관리를 알아보는 구간이에요.',
    },
    {
      id: 'weekly-week-nav',
      mascot:
        '주차 선택 메뉴와 이전·다음 버튼으로 원하는 주를 고를 수 있어요. 바꿔 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-weekly-learning-guide-week-select-input, .cp-weekly-learning-guide-week-nav',
      manualAdvance: true,
    },
    {
      id: 'weekly-home-notice-btn',
      mascot: '"가정통신 관리"를 눌러 모달을 열어 주세요.',
      targetSelector: '.cp-weekly-learning-guide-home-notice-btn',
    },
    {
      id: 'weekly-home-notice-modal',
      mascot:
        '주별로 가정통신 메시지를 입력·관리할 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '.cp-weekly-learning-guide-modal:not(.cp-weekly-learning-guide-track-color-modal)',
      advanceWhenSelectorGone:
        '.cp-weekly-learning-guide-modal:not(.cp-weekly-learning-guide-track-color-modal)',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'weekly-track-color-btn',
      mascot: '이번에는 "시간표 색상 관리"를 눌러 모달을 열어 주세요.',
      targetSelector: '.cp-weekly-learning-guide-track-color-btn',
      section: '[주간학습 안내] 시간표 색상 관리',
      sectionMascot: '편제별로 표현할 색상을 설정하는 구간이에요.',
    },
    {
      id: 'weekly-track-color-modal',
      mascot:
        '여기서 편제별로 주간학습 안내에 표시할 색상을 설정할 수 있어요. 확인했으면 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '.cp-weekly-learning-guide-track-color-modal',
      advanceWhenSelectorGone: '.cp-weekly-learning-guide-track-color-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'weekly-neis-toggle-on',
      mascot:
        'NEIS 반별시간표 스위치를 ON으로 켜 주세요. 이미 ON이면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-weekly-learning-guide-neis-toggle-wrap .team-auto-approve-toggle',
      advanceWhenSelectorExists: '.cp-weekly-learning-guide-neis-legend',
      manualAdvanceIfExists: '.cp-weekly-learning-guide-neis-toggle-wrap .team-auto-approve-toggle.on',
      section: '[주간학습 안내] NEIS 반별시간표 양식 활용',
      sectionMascot: 'NEIS 반별시간표 모드·표시 의미·양식 활용을 익히는 구간이에요.',
    },
    {
      id: 'weekly-neis-legend-diff',
      mascot:
        '회색 표시는 기초 시간표에서 변경된 영역이에요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-neis-legend="diff-track"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'weekly-neis-legend-dedicated',
      mascot:
        '노란 표시는 기초 시간표와 같은 편제지만 색상(전담 과목 등)이 다른 영역이에요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-neis-legend="dedicated-mismatch"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'weekly-neis-legend-unregistered',
      mascot:
        '빨간 텍스트는 편제에 등록되지 않은 시간표를 뜻해요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-neis-legend="unregistered"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'weekly-neis-legend-short',
      mascot:
        '초록 테두리는 요일별 기준 수업시수에서 삭제되거나 추가된 영역이에요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-neis-legend="short-edge"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'weekly-neis-xls-export',
      mascot:
        'NEIS 반별시간표 모드에서 xls를 내려받으면, NEIS에 반별시간표를 입력할 때 기초시간표에서 바꿀 부분만 쉽게 찾아 편리하게 변경할 수 있어요. 버튼 위치를 확인한 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-weekly-learning-guide-document-toolbar [aria-label="xls 다운"], .cp-weekly-learning-guide-header [aria-label="xls 다운"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'outro',
      mascot:
        '기록 모아보기와 주간학습 안내 튜토리얼을 마쳤어요. 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const recordMemosWeeklyGuide: TutorialDefinition = {
  ...recordMemosWeeklyGuideRaw,
  steps: recordMemosWeeklyGuideRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    const targetMissingHint = buildTutorialTargetMissingHint(recover, step.targetMissingHint);
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint,
    };
  }),
};

export default recordMemosWeeklyGuide;
